import { Link } from "react-router-dom";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "dark";
type Size = "md" | "lg";

const variantClass: Record<Variant, string> = {
  primary:
    "bg-teal text-ink shadow-glow hover:bg-teal-glow active:translate-y-px",
  secondary:
    "bg-surface-raised text-fg border border-border hover:border-teal/50 hover:bg-surface-muted",
  ghost: "bg-transparent text-fg hover:bg-surface-muted",
  dark: "bg-ink text-fg-inverse hover:bg-ink-soft",
};

const sizeClass: Record<Size, string> = {
  md: "min-h-11 px-5 text-sm",
  lg: "min-h-12 px-6 text-base",
};

type Base = {
  children: ReactNode;
  className?: string;
  variant?: Variant;
  size?: Size;
};

type ButtonAsButton = Base &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "children"> & {
    to?: undefined;
    href?: undefined;
  };

type ButtonAsLink = Base & {
  to: string;
  href?: undefined;
  onClick?: () => void;
};

type ButtonAsAnchor = Base & {
  href: string;
  to?: undefined;
  target?: string;
  rel?: string;
  onClick?: () => void;
};

export type ButtonProps = ButtonAsButton | ButtonAsLink | ButtonAsAnchor;

function classesFor(variant: Variant, size: Size, className?: string) {
  return cn(
    "inline-flex items-center justify-center gap-2 rounded-full font-semibold transition duration-200 cursor-pointer disabled:cursor-not-allowed disabled:opacity-60",
    variantClass[variant],
    sizeClass[size],
    className,
  );
}

export function Button(props: ButtonProps) {
  const variant = props.variant ?? "primary";
  const size = props.size ?? "md";
  const classes = classesFor(variant, size, props.className);

  if (props.to) {
    return (
      <Link to={props.to} className={classes} onClick={props.onClick}>
        {props.children}
      </Link>
    );
  }

  if (props.href) {
    return (
      <a
        href={props.href}
        className={classes}
        target={props.target}
        rel={props.rel}
        onClick={props.onClick}
      >
        {props.children}
      </a>
    );
  }

  const buttonProps = props as ButtonAsButton;
  return (
    <button
      type={buttonProps.type ?? "button"}
      className={classes}
      disabled={buttonProps.disabled}
      onClick={buttonProps.onClick}
      name={buttonProps.name}
      value={buttonProps.value}
      form={buttonProps.form}
      aria-label={buttonProps["aria-label"]}
    >
      {props.children}
    </button>
  );
}
